<template>
    <v-app class="admin-home-view">
        <v-navigation-drawer v-model="drawer" app clipped class="navigation-drawer">
            <v-list dense>
                <v-list-item-group :value="activeTab">
                    <v-list-item v-for="(menu, index) in menus" :key="index" @click="goto(menu.path)">
                        <v-list-item-action>
                            <v-icon>{{ menu.icon }}</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>{{ menu.title }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-icon v-if="menu.title === 'Comments' && showBadge">
                            <div class="comment-badge">
                                <v-icon size="34" color="red" class="icon">mdi-circle</v-icon>
                                <span class="count">{{ commentsUtils.unapproved }}</span>
                            </div>
                        </v-list-item-icon>
                    </v-list-item>
                </v-list-item-group>
            </v-list>
            <template v-slot:append>
                <div class="pa-1">
                    <v-btn text plain class="text-caption font-weight-bold" @click.prevent="$router.push('/')" :link="true" :ripple="false">
                        <span class="mr-1">otisak.org</span>
                    </v-btn>
                </div>
            </template>
        </v-navigation-drawer>

        <v-app-bar app clipped-left :color="dark ? 'grey darken-4' : 'grey lighten-3'" elevation="3">
            <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
            <v-toolbar-title class="pl-3">
                <Logo :size="[32, 1.2]" :color="dark ? '#ffffff' : '#1969d2'" />
            </v-toolbar-title>
            <v-spacer />
            <v-btn text icon @click="handleTheme">
                <v-icon>{{ dark ? 'mdi-brightness-4' : 'mdi-brightness-6'}}</v-icon>
            </v-btn>
            <v-menu
                v-if="user"
                bottom
                min-width="200px"
                rounded
                offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn icon x-large v-on="on">
                        <v-avatar size="34">
                            <v-img v-if="user.data.photoURL" :src="user.data.photoURL" />
                            <v-icon v-else>mdi-account</v-icon>
                        </v-avatar>
                    </v-btn>
                </template>
                <v-card>
                    <v-list-item-content class="justify-center">
                        <div class="mx-auto text-center">
                            <v-avatar color="primary" class="mb-3">
                                <v-img v-if="user.data.photoURL" :src="user.data.photoURL" />
                                <v-icon v-else>mdi-account</v-icon>
                            </v-avatar>
                            <h3>{{ user.data.displayName }}</h3>
                            <p class="text-caption mt-1">{{ user.data.email }}</p>
                            <v-divider class="my-3"></v-divider>
                            <v-btn block depressed text @click="editItem">Edit Account</v-btn>
                            <v-divider class="my-3"></v-divider>
                            <v-btn block depressed text @click="logOut">Logout</v-btn>
                        </div>
                    </v-list-item-content>
                </v-card>
            </v-menu>
        </v-app-bar>

        <v-main>
            <v-container fluid>
                <v-row>
                    <v-col>
                        <router-view />
                    </v-col>
                </v-row>
            </v-container>
        </v-main>

        <v-dialog
            v-if="user.loggedIn"
            v-model="editUser"
            persistent
            max-width="600px">
            <v-card id="user-management-view-edit">
                <v-card-title>
                    <span class="py-2 text-h5">Edit Account</span>
                </v-card-title>
                <v-card-text>
                    <v-form ref="dialogForm" v-model="valid" lazy-validation>
                        <v-container class="pa-1">
                            <v-row>
                                <v-col cols="12" md="4" align="center" align-self="center">
                                    <input
                                        type="file"
                                        class="d-none"
                                        ref="uploader"
                                        accept="image/*"
                                        @change="selectFile">
                                    <v-img
                                        :lazy-src="editedItem.photoURL"
                                        :src="editedItem.photoURL"
                                        height="120"
                                        width="120"
                                        class="fill-height profile-pic rounded-circle"
                                        @click="$refs.uploader.click()">
                                        <template v-slot:placeholder>
                                            <v-avatar class="fill-height" color="primary" width="120" height="120" rounded>
                                                <v-icon color="white" large v-if="!loading">mdi-upload</v-icon>
                                                <v-progress-circular indeterminate color="white" v-else />
                                            </v-avatar>
                                        </template>
                                    </v-img>
                                </v-col>
                                <v-col cols="12" md="8">
                                    <v-text-field
                                        v-model="editedItem.displayName"
                                        :rules="nameRules"
                                        label="Name"
                                        required>
                                    </v-text-field>
                                    <v-text-field
                                        v-model="editedItem.title"
                                        label="Job Title"
                                        required>
                                    </v-text-field>
                                    <v-text-field
                                        v-model="editedItem.email"
                                        label="E-mail"
                                        readonly>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12">
                                    <v-card elevation="0" outlined>
                                        <v-card-text class="d-flex justify-space-between align-center py-2 px-3">
                                            <span v-if="!editedItem.emailVerified">
                                                Your e-mail is not verified. Please verify!
                                            </span>
                                            <span v-else>
                                                Your e-mail is verified. Thanks for being a kind human being.
                                            </span>
                                            <v-btn depressed @click="verifyEmail" v-if="!editedItem.emailVerified">
                                                Verify
                                                <v-icon right>mdi-email-fast</v-icon>
                                            </v-btn>
                                            <v-icon color="success" large v-else>mdi-check-circle</v-icon>
                                        </v-card-text>
                                    </v-card>
                                    <v-text-field
                                        v-model="editedItem.linkedin"
                                        elevation="0"
                                        class="mt-4"
                                        placeholder="https://www.linkedin.com/in/yourname"
                                        prepend-inner-icon="mdi-linkedin">
                                    </v-text-field>
                                    <v-textarea
                                        v-model="editedItem.bio"
                                        label="About me"
                                        name="about me"
                                        rows="3"
                                        filled
                                        outlined
                                        auto-grow
                                        hide-details>
                                    </v-textarea>
                                </v-col>
                                <v-col cols="12">
                                    <v-card-actions class="pa-0">
                                        <v-spacer></v-spacer>
                                        <v-btn :disabled="loading" plain @click="close">Close</v-btn>
                                        <v-btn :disabled="loading" color="primary" @click="save">Save</v-btn>
                                    </v-card-actions>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            <span>{{ snackText }}</span>
            <template v-slot:action="{ attrs }">
                <v-icon v-bind="attrs" text @click="snack = false">mdi-close</v-icon>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import Logo from '@/components/Logo';
import { mapGetters, mapActions } from 'vuex';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import {showSnack} from "@/components/mixins/showSnack";

export default {
    name: 'AdminHome',

    components: {
        Logo
    },

    mixins: [showSnack],

    data() {
        return {
            dark: false,
            path: '/dashboard',
            drawer: null,
            loading: false,
            editUser: false,
            valid: true,
            editedIndex: -1,
            editedItem: {},
            nameRules: [
                v => v ? !!v || 'Name is required' : true
            ],
            passwordRules: [
                v => this.editedIndex < 0 ? !!v || 'Password is required' : true,
                v => this.editedIndex < 0 ? (v && v.length > 6) || 'Must be longer than 6 characters' : true
            ],

            menus: [
                { path: '', title: 'Dashboard', icon: 'mdi-view-dashboard' },
                { path: '/companies', title: 'Companies', icon: 'mdi-folder-multiple' },
                { path: '/comments', title: 'Comments', icon: 'mdi-comment-text-multiple' },
                { path: '/user-management', title: 'User Management', icon: 'mdi-account-multiple', admin: true },
                { path: '/settings', title: 'Settings', icon: 'mdi-cog', admin: true }
            ]
        }
    },

    created() {
        this.dark = localStorage.getItem('darkTheme') === 'true' || false;
        this.displayMenuPerRole();
        this.getCompaniesUtils();
        this.getCommentsUtils();

        if (this.mdAndUp) {
            window.addEventListener('resize', () => {
                const vh = window.innerHeight * 0.01;
                document.querySelector('.navigation-drawer').style.setProperty('--vh', `${vh}px`);
            });
        }
    },

    beforeDestroy() {
        this.$vuetify.theme.dark = this.dark = false;
    },

    watch: {
        dark() {
            this.$vuetify.theme.dark = this.dark;
        }
    },

    computed: {
        ...mapGetters({
            user: 'auth/user',
            commentsUtils: 'dashboard/commentsUtils'
        }),
        mdAndUp() {
            return this.$vuetify.breakpoint.mdAndUp;
        },
        activeTab() {
            return this.menus.findIndex((elem, i) => {
                if (this.path + elem.path === this.$route.path ||
                    this.path + elem.path + '/' === this.$route.path) {
                    return i;
                }
            });
        },
        showBadge() {
            return this.commentsUtils ? this.commentsUtils.unapproved > 0 : false;
        }
    },

    methods: {
        ...mapActions({
            fetchUser: 'auth/fetchUser',
            getCompaniesUtils: 'dashboard/getCompaniesUtils',
            getCommentsUtils: 'dashboard/getCommentsUtils'
        }),
        handleTheme() {
            this.dark = !this.dark
            localStorage.setItem('darkTheme', this.dark);
        },
        async displayMenuPerRole() {
            this.user.data.getIdTokenResult().then(user => {
                this.menus = this.menus.filter(menu => menu.admin ? user.claims.admin : true);
            });
        },
        getUserBio() {
            const vm = this;
            firebase.firestore().collection(process.env.VUE_APP_COLLECTION_USERS)
                .doc(this.user.data.uid)
                .get()
                .then(doc => {
                    const { bio, linkedin, title } = doc.exists ? doc.data() : { bio: '', linkedin: '', title: '' };
                    vm.editedItem.bio = typeof bio !== 'undefined' ? bio : '';
                    vm.editedItem.linkedin = typeof linkedin !== 'undefined' ? linkedin : '';
                    vm.editedItem.title = typeof title !== 'undefined' ? title : '';
                });
        },
        goto(newPath) {
            this.$router.push({path: this.path + newPath}).catch(() => {});
        },
        editItem() {
            this.getUserBio();
            const { displayName, email, emailVerified, photoURL } = this.user.data;
            this.editedIndex = 0;
            this.editedItem = Object.assign({}, {
                displayName, email, emailVerified, photoURL,
                bio: this.editedItem.bio,
                linkedin: this.editedItem.linkedin,
                title: this.editedItem.title
            });
            this.editUser = true;
        },
        verifyEmail() {
            this.user.data.sendEmailVerification()
                .then(() => {
                    this.showSnack('Check your email!', 'success');
                })
                .catch(() => {
                    this.showSnack('Darn! Why don\'t you try again?', 'error');
                });
        },
        save() {
            const { displayName, photoURL, bio, linkedin, title } = this.editedItem;
            this.user.data.updateProfile({ displayName, photoURL })
                .then(() => {
                    firebase.firestore().collection(process.env.VUE_APP_COLLECTION_USERS)
                        .doc(this.user.data.uid)
                        .set({ displayName, photoURL, bio, linkedin, title, display: true });
                })
                .then(() => {
                    this.showSnack('Your account is updated!', 'success');
                    this.close();
                })
                .catch(() => {
                    this.showSnack('Oops! Something went wrong.', 'error');
                });
        },
        close() {
            this.editUser = false;
            this.$nextTick(() => {
                this.editedItem = {}
                this.editedIndex = -1;
            });
        },
        selectFile() {
            const file = this.$refs.uploader.files[0];
            if (file.size > 1000000) {
                this.showSnack('Image size should be under 1MB', 'error');
                return;
            }
            if (file && file.name) {
                this.loading = true;
                const ref = firebase.storage().ref().child(`users_images/${file.name}`);
                const vm = this;
                ref.put(file).then(snapshot => {
                    this.loading = false;
                    if (snapshot.state === 'success') {
                        snapshot.ref.getDownloadURL().then(downloadURL => {
                            vm.editedItem.photoURL = downloadURL;
                        });
                    } else {
                        this.showSnack('Image could not be uploaded', 'error');
                    }
                });
            }
        },
        async logOut() {
            await firebase.auth().signOut()
                .then(() => {
                    this.close();
                    this.fetchUser(false);
                })
                .then(() => {
                    this.$router.replace({path: '/login'}).catch(() => {});
                });
        }
    }
}
</script>

<style lang="scss">
.admin-home-view .navigation-drawer {
    height: calc(var(--vh, 1vh) * 100) !important;
}

.admin-home-view {
    .comment-badge {
        position: relative;
        width: 32px;
        height: 32px;

        .icon,
        .count {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .count {
            color: #ffffff;
            font-size: 12px;
            font-weight: bold;
        }
    }
}

.profile-pic {
    border-radius: 50%;
    transition: 0.2s ease-in;
    &:hover {
        opacity: 0.9;
        cursor: pointer;
    }
}
</style>
